import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, ArrowDown, ArrowDownLeft, ArrowDownUp, ArrowRight, ArrowUpRight, Check, ChevronDown, ChevronsDownUp,
  ChevronsUpDown, CornerUpLeft, Eye, Folder, History, Image, MailPlus, Maximize2, MessagesSquare, Minimize2,
  MousePointerClick, Paperclip, RefreshCw, Send,
} from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { EmailBody } from '../EmailBody';
import {
  buildThreads, displayName, hasReplyPrefix, hueOf, initialsOf, parseAddress, relativeParts, sameOfficeDay, snippetOf, splitAddresses,
  type EmailThread as Thread, type ReplyMeta,
} from '../../lib/emailThreads';
import './email-thread.css';

/**
 * EL HILO DE CORREOS, UNO SOLO PARA TODA LA APP.
 *
 * Reporte del equipo (2026-09-02): "es muy difícil trabajar, visualmente no ayuda". Lo concreto:
 * el historial abría en una cajita de 240px, los correos eran cuadros grises iguales sin saber
 * dónde empezaba uno y terminaba otro, quién lo mandó y si lo abrieron estaba en gris al pie, y
 * no había forma de saber cuál era respuesta de cuál. Y había CINCO versiones distintas de la
 * misma lista (el box del claim, el chat, el buzón, la pestaña del homeowner, la línea de tiempo).
 *
 * Esta es la única. Lo que decide cada cosa:
 *  · CONVERSACIONES: se agrupa por asunto (lib/emailThreads). El que abre la conversación lleva
 *    "Email nuevo"; los demás, "Respuesta" con a quién responden. Un riel une la conversación.
 *  · FONDO por dirección: enviado = índigo, recibido = verde. Se ve de lejos.
 *  · CABECERA siempre visible: quién → a quién, la fecha en negrita y, en los enviados, el
 *    seguimiento enviado → entregado → abierto → link, con la hora de cada paso.
 *  · Cada correo se RECOGE o DESPLIEGA; el más reciente arranca abierto, el resto cerrado.
 *  · Más reciente ARRIBA (se puede invertir). Panel alto + pantalla completa.
 *
 * `variant="panel"` = caja con barra y scroll propio (el box del claim). `variant="inline"` =
 * solo la lista, para quien ya tiene su propio scroll (el buzón, un modal).
 */

export type EmailAttachment = { id: string; file_name: string | null; mime_type?: string | null };
export type EmailLike = {
  id: string; direction: string; recipient_type?: string | null;
  from_address?: string | null; from_name?: string | null; to_address?: string | null; cc_address?: string | null;
  subject?: string | null; body?: string | null; status?: string | null; created_at: string;
  /** Quién de la oficina lo mandó (nombre del usuario). */
  author?: string | null;
  delivered_at?: string | null; opened_at?: string | null; clicked_at?: string | null;
  attachments?: EmailAttachment[] | null;
};
export type RecipientCategory = 'insurance' | 'contractor' | 'homeowner';

const CATS: RecipientCategory[] = ['insurance', 'contractor', 'homeowner'];
// 'complained' también: el destinatario lo marcó como spam (webhook de Resend) y la dirección quedó suprimida.
const BAD_STATUS = new Set(['suppressed', 'bounced', 'failed', 'complained']);
const isImg = (n: string | null | undefined) => /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(n ?? '');
const TZ = 'America/New_York';

/** Estado abierto/cerrado durable (localStorage), mismo patrón que ui/Module. */
function useDurable<T extends string | boolean>(key: string | undefined, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    if (!key) return initial;
    try { const raw = localStorage.getItem(key); return raw == null ? initial : (JSON.parse(raw) as T); } catch { return initial; }
  });
  useEffect(() => { if (!key) return; try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* modo privado */ } }, [key, v]);
  return [v, setV];
}

/** Fechas en hora de la OFICINA (Este), en el idioma de la app. */
function useFmt() {
  const { lang, t } = useI18n();
  const loc = lang === 'es' ? 'es-US' : 'en-US';
  const safe = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? null : d; };
  return {
    when: (iso: string) => safe(iso)?.toLocaleString(loc, { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) ?? '—',
    full: (iso: string) => safe(iso)?.toLocaleString(loc, { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) ?? '—',
    /** Solo la hora si cae el mismo día que `ref`; si no, día + hora. */
    clock: (iso: string, ref: string) => {
      const d = safe(iso); if (!d) return '—';
      return sameOfficeDay(iso, ref)
        ? d.toLocaleTimeString(loc, { timeZone: TZ, hour: 'numeric', minute: '2-digit' })
        : d.toLocaleString(loc, { timeZone: TZ, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    ago: (iso: string, now: number) => { const { unit, n } = relativeParts(iso, now); return unit === 'now' ? t('et.justNow') : t(`et.ago_${unit}`, { n }); },
  };
}

/** Quién firma el correo: el usuario de la app en los enviados; el remitente real en los recibidos. */
function senderOf(m: EmailLike, staffFallback: string): string {
  if (m.direction === 'out') return m.author?.trim() || (m.from_address ? displayName(m.from_address) : staffFallback);
  return displayName(m.from_address, m.from_name);
}
/** La otra punta: a quién se le mandó (primer destinatario) o de quién vino. */
function counterpartOf(m: EmailLike): string {
  const raw = m.direction === 'out' ? splitAddresses(m.to_address)[0] : m.from_address;
  return parseAddress(raw ?? null).email.toLowerCase();
}

/* ------------------------------------------------------------------ seguimiento del enviado */

function Tracker({ m }: { m: EmailLike }) {
  const { t } = useI18n();
  const { clock } = useFmt();
  if (m.status && BAD_STATUS.has(m.status)) {
    return <span className="et-bad"><AlertTriangle size={11} strokeWidth={2} /> {t(`em.st_${m.status}`)}</span>;
  }
  const steps: Array<{ k: string; label: string; at: string | null | undefined; cls: string; Icon: typeof Send }> = [
    { k: 'sent', label: t('et.sent'), at: m.created_at, cls: 'done', Icon: Send },
  ];
  // "Aún no lo abren" es una AFIRMACIÓN, y solo es cierta si el correo salió por un canal con
  // tracking (Resend). Un enviado que bajó del IMAP de la casilla, o un evento de la línea de
  // tiempo sin esos campos, no tiene forma de saberlo: ahí se muestra solo "Enviado · hora".
  const rastreado = Boolean(m.delivered_at || m.opened_at || m.clicked_at);
  if (rastreado) {
    steps.push({ k: 'delivered', label: t('et.delivered'), at: m.delivered_at, cls: m.delivered_at ? 'done' : '', Icon: Check });
    steps.push({ k: 'opened', label: m.opened_at ? t('et.opened') : t('et.notOpened'), at: m.opened_at, cls: m.opened_at ? 'opened' : '', Icon: Eye });
    if (m.clicked_at) steps.push({ k: 'clicked', label: t('et.clicked'), at: m.clicked_at, cls: 'clicked', Icon: MousePointerClick });
  }
  return (
    <div className="et-track" title={t('et.trackHint')}>
      {steps.map((s, i) => (
        <Fragment key={s.k}>
          {i > 0 && <span className={`et-link${s.at ? ' done' : ''}`} style={{ animationDelay: `${i * 110}ms` }} />}
          <span className={`et-step ${s.cls}`}>
            <span className="ic"><s.Icon size={10} strokeWidth={2.75} /></span>
            {s.label}
            {s.at && <span className="t">· {clock(s.at, m.created_at)}</span>}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ una tarjeta */

function MessageCard({ m, meta, open, onToggle, showSubject, highlight, delay, now, onRecategorize, onAddToFiles, onReply, busy, cardRef }: {
  m: EmailLike; meta: ReplyMeta<EmailLike> | undefined; open: boolean; onToggle: () => void; showSubject: boolean;
  highlight: boolean; delay: number; now: number;
  onRecategorize?: (id: string, cat: RecipientCategory) => void | Promise<void>;
  onAddToFiles?: (id: string) => void | Promise<void>;
  /** Si viene, cada correo trae "Reply": responder ESE correo, en su misma cadena (dueño 2026-10-19). */
  onReply?: (m: EmailLike) => void;
  busy: boolean;
  cardRef?: (el: HTMLElement | null) => void;
}) {
  const { t } = useI18n();
  const { when, full, ago } = useFmt();
  const out = m.direction === 'out';
  const sender = senderOf(m, t('et.staff'));
  const toList = splitAddresses(m.to_address);
  const to0 = toList[0] ? parseAddress(toList[0]) : null;
  const toLabel = to0 ? (to0.name ?? to0.email) : '—';
  const atts = m.attachments ?? [];
  const parentSender = meta?.parent ? senderOf(meta.parent, t('et.staff')) : null;
  const avatarBg = out ? 'var(--et-out)' : `hsl(${hueOf(parseAddress(m.from_address).email || sender)} 52% 44%)`;
  const snippet = open ? '' : snippetOf(m.body);
  return (
    <article ref={cardRef} className={`et-msg ${out ? 'out' : 'in'}${open ? ' open' : ''}${highlight ? ' hl' : ''}`} style={{ animationDelay: `${Math.min(delay, 14) * 28}ms` }} data-email-id={m.id}>
      <span className="et-dot" aria-hidden />
      <div className="et-head" role="button" tabIndex={0} aria-expanded={open} onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}>
        <span className="et-avatar" style={{ background: avatarBg }} title={sender}>{initialsOf(sender)}</span>
        <div className="et-main">
          <div className="et-line1">
            <span className="et-dir">{out ? <><ArrowUpRight size={11} strokeWidth={2.5} /> {t('et.sent')}</> : <><ArrowDownLeft size={11} strokeWidth={2.5} /> {t('et.received')}</>}</span>
            <span className="et-name">{sender}</span>
            {/* Flecha + destinatario van JUNTOS: en columnas angostas la flecha quedaba sola en su
                renglón; y el "+1 más" vive fuera de los puntos suspensivos para que nunca se corte. */}
            <span className="et-to" title={m.to_address ?? ''}>
              <span className="et-arrow"><ArrowRight size={13} strokeWidth={2} /></span>
              <span className="et-addr">{toLabel}</span>
              {toList.length > 1 && <span className="more">{t('et.moreRecipients', { n: toList.length - 1 })}</span>}
            </span>
            {meta?.orphanReply ? (
              <span className="et-kind orphan" title={t('et.orphanReply')}><CornerUpLeft size={11} strokeWidth={2.25} /> {t('et.reply')}</span>
            ) : meta?.isReply ? (
              <span className="et-kind reply" title={parentSender && meta.parent ? t('et.inReplyTo', { who: parentSender, when: when(meta.parent.created_at) }) : t('et.reply')}>
                <CornerUpLeft size={11} strokeWidth={2.25} /> {t('et.reply')}
              </span>
            ) : (
              <span className="et-kind new" title={t('et.original')}><MailPlus size={11} strokeWidth={2.25} /> {t('et.new')}</span>
            )}
          </div>
          {showSubject && <div className="et-subj" title={m.subject ?? ''}>{m.subject?.trim() || t('et.noSubject')}</div>}
          {out && <Tracker m={m} />}
          {!open && snippet && <div className="et-snippet">{snippet}</div>}
        </div>
        <div className="et-right">
          <span className="et-when" title={full(m.created_at)}>{when(m.created_at)}</span>
          <span className="et-ago">{ago(m.created_at, now)}</span>
          {atts.length > 0 && <span className="et-atts-n" title={t('et.attachments', { n: atts.length })}><Paperclip size={11} strokeWidth={2} /> {atts.length}</span>}
          {onReply && (
            <button type="button" className="et-reply-btn" title={t('et.replyBtn')}
              onClick={(e) => { e.stopPropagation(); onReply(m); }}>
              <CornerUpLeft size={12} strokeWidth={2.25} /> {t('et.reply')}
            </button>
          )}
          <span className="et-caret"><ChevronDown size={15} strokeWidth={2} /></span>
        </div>
      </div>
      {open && (
        <div className="et-body">
          <div className="et-meta">
            <div><b>{t('et.from')}:</b> {m.from_address || (out ? sender : '—')}</div>
            <div><b>{t('et.to')}:</b> {m.to_address || '—'}</div>
            {m.cc_address && <div><b>{t('et.cc')}:</b> {m.cc_address}</div>}
            <div>{out && m.author ? <><b>{t('em.by')}</b> {m.author} · </> : null}{full(m.created_at)}</div>
            {meta?.parent && parentSender && (
              <div className="et-reply-of"><CornerUpLeft size={11} strokeWidth={2.25} /> {t('et.inReplyTo', { who: parentSender, when: when(meta.parent.created_at) })}</div>
            )}
          </div>
          {m.body?.trim() ? <div className="et-paper"><EmailBody body={m.body} /></div> : null}
          {(atts.length > 0 || onRecategorize) && (
            <div className="et-actions">
              {atts.length > 0 && (
                <div className="et-atts">
                  {atts.map((a) => (
                    <span key={a.id} className="et-att" title={a.file_name || 'file'}>
                      <a href={`/api/emails/attachments/${a.id}`} target="_blank" rel="noopener noreferrer">
                        {isImg(a.file_name) ? <Image size={12} strokeWidth={1.75} /> : <Paperclip size={12} strokeWidth={1.75} />} {a.file_name || 'file'}
                      </a>
                      <a href={`/api/emails/attachments/${a.id}?download=1`} title={t('em.download')}><ArrowDown size={12} strokeWidth={1.75} /></a>
                    </span>
                  ))}
                  {onAddToFiles && (
                    <button type="button" className="btn ghost sm" disabled={busy} onClick={() => { void onAddToFiles(m.id); }}>
                      {busy ? '…' : <><Folder size={12} strokeWidth={1.75} /> {t('em.addToFiles')}</>}
                    </button>
                  )}
                </div>
              )}
              {onRecategorize && (
                <select className="input" title={t('em.recatHint')} disabled={busy}
                  value={m.recipient_type && (CATS as string[]).includes(m.recipient_type) ? m.recipient_type : ''}
                  onChange={(e) => { const v = e.target.value as RecipientCategory | ''; if (v) void onRecategorize(m.id, v); }}
                  style={{ marginLeft: 'auto' }}>
                  <option value="" disabled>↔ {t('em.recatMove')}</option>
                  {CATS.map((c) => <option key={c} value={c}>{t(`em.${c}`)}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ una tarjeta suelta */

/**
 * UN correo solo, con la misma tarjeta del hilo: para donde se muestra un email puntual (la nota
 * "Email sent…" del chat del claim, la línea de tiempo del homeowner, el modal de la timeline de
 * pagos). Sin barra ni agrupación; el chip dice "Respuesta" si el asunto trae "Re:", y como no hay
 * hilo alrededor nunca se marca como huérfano.
 */
export function EmailCard({ message, defaultOpen = true, showSubject = true, onRecategorize, onAddToFiles, onReply, busy }: {
  message: EmailLike; defaultOpen?: boolean; showSubject?: boolean;
  onRecategorize?: (id: string, cat: RecipientCategory) => void | Promise<void>;
  onAddToFiles?: (id: string) => void | Promise<void>;
  onReply?: (m: EmailLike) => void;
  busy?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [now] = useState(() => Date.now());
  const meta: ReplyMeta<EmailLike> = { threadKey: `#${message.id}`, index: 1, total: 1, isReply: hasReplyPrefix(message.subject), parent: null, orphanReply: false };
  return (
    <div className="et-panel et-inline">
      <div className="et-thread-list single">
        <MessageCard m={message} meta={meta} open={open} onToggle={() => setOpen((v) => !v)} showSubject={showSubject} highlight={false} delay={0} now={now}
          onRecategorize={onRecategorize} onAddToFiles={onAddToFiles} onReply={onReply} busy={Boolean(busy)} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ el hilo */

export function EmailThread({
  messages, loading, error, onRetry, emptyText, variant = 'panel', title, defaultExpanded = 'latest', highlightId,
  onRecategorize, onAddToFiles, onReply, busyId, group = true, collapsible = false, persistKey, defaultOpenPanel = true, maxHeight,
}: {
  messages: EmailLike[];
  loading?: boolean;
  /** Fallo de carga: se dice, no se disfraza de "sin correos" (ley: vacío ≠ fallido). */
  error?: boolean;
  onRetry?: () => void;
  emptyText?: string;
  variant?: 'panel' | 'inline';
  title?: ReactNode;
  /** Qué arranca desplegado: el más reciente (default), todos, o ninguno. */
  defaultExpanded?: 'latest' | 'all' | 'none';
  /** Correo a resaltar y desplegar (deep-link desde una nota del claim). */
  highlightId?: string | null;
  /** Si viene, cada correo trae el "Mover a…" (Seguro/Contratista/Homeowner). */
  onRecategorize?: (id: string, cat: RecipientCategory) => void | Promise<void>;
  /** Si viene, los correos con adjuntos traen "Agregar a files del claim". */
  onAddToFiles?: (id: string) => void | Promise<void>;
  /** Si viene, cada correo trae "Reply" (responder ese correo en su cadena). */
  onReply?: (m: EmailLike) => void;
  busyId?: string | null;
  /** false = lista plana sin agrupar por conversación (para un hilo que YA es una conversación). */
  group?: boolean;
  /** El panel entero se puede recoger desde su barra (el box del claim arranca recogido). */
  collapsible?: boolean;
  /** Llave para recordar recogido/desplegado entre visitas. */
  persistKey?: string;
  defaultOpenPanel?: boolean;
  maxHeight?: string;
}) {
  const { t } = useI18n();
  const { ago } = useFmt();
  const [panelOpen, setPanelOpen] = useDurable<boolean>(persistKey ? `et:open:${persistKey}` : undefined, defaultOpenPanel);
  const [order, setOrder] = useDurable<'newest' | 'oldest'>('et:order', 'newest');
  const [max, setMax] = useState(false);
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const [now, setNow] = useState(() => Date.now());
  const seen = useRef<Set<string> | null>(null);
  const cards = useRef(new Map<string, HTMLElement>());

  // "Hace 3 min" se actualiza solo; no hace falta recargar para que el reloj avance.
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 60_000); return () => clearInterval(i); }, []);

  // group=false: quien llama ya sabe que esto es UNA conversación (el lector del buzón) → se anota
  // como tal (nuevo/respuesta por dirección o "Re:"), sin volver a partirla por asunto.
  const { threads, meta } = useMemo(() => buildThreads(messages, { flat: !group }), [messages, group]);
  const newestId = threads[0]?.messages[threads[0].messages.length - 1]?.id ?? null;

  // Qué arranca abierto: en la primera carga según `defaultExpanded` (+ el resaltado); después,
  // solo lo que llega NUEVO (p. ej. el correo que acabo de mandar) se abre solo.
  useEffect(() => {
    if (!messages.length) return;
    const ids = messages.map((m) => m.id);
    if (!seen.current) {
      seen.current = new Set(ids);
      const init = new Set<string>(defaultExpanded === 'all' ? ids : defaultExpanded === 'latest' && newestId ? [newestId] : []);
      if (highlightId) init.add(highlightId);
      setOpenIds(init);
      return;
    }
    const fresh = ids.filter((id) => !seen.current!.has(id));
    if (fresh.length) {
      for (const id of fresh) seen.current.add(id);
      if (newestId && fresh.includes(newestId)) setOpenIds((s) => new Set(s).add(newestId));
    }
  }, [messages, newestId, defaultExpanded, highlightId]);

  // Deep-link: llevar el correo resaltado a la vista.
  useEffect(() => {
    if (!highlightId) return;
    const el = cards.current.get(highlightId);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, messages]);

  // Esc cierra la pantalla completa.
  useEffect(() => {
    if (!max) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [max]);

  const toggle = (id: string) => setOpenIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const expandAll = () => setOpenIds(new Set(messages.map((m) => m.id)));
  const collapseAll = () => setOpenIds(new Set());
  const allOpen = messages.length > 0 && messages.every((m) => openIds.has(m.id));

  // En modo plano buildThreads ya devolvió una sola conversación; sin cabecera de hilo (multi=false).
  const shown: Thread<EmailLike>[] = threads;
  let delay = 0;

  const body = (
    <div className="et-scroll" style={maxHeight ? { ['--et-max' as string]: maxHeight } : undefined}>
      {loading && !messages.length ? (
        <><div className="et-skel" /><div className="et-skel" style={{ opacity: 0.7 }} /><div className="et-skel" style={{ opacity: 0.4 }} /></>
      ) : error && !messages.length ? (
        <div className="et-fail">
          <AlertTriangle size={15} strokeWidth={2} /> {t('et.loadFailed')}
          {onRetry && <button type="button" className="btn sm" onClick={onRetry}><RefreshCw size={12} strokeWidth={2} /> {t('common.retry')}</button>}
        </div>
      ) : !messages.length ? (
        <div className="et-empty">{emptyText ?? t('em.noHistory')}</div>
      ) : shown.map((th) => {
        const multi = group && th.messages.length > 1;
        const list = order === 'newest' ? [...th.messages].reverse() : th.messages;
        // Con quién es la conversación: las otras puntas, sin repetir.
        const people = multi ? [...new Set(th.messages.map(counterpartOf).filter(Boolean))].slice(0, 3) : [];
        const lastAt = th.messages[th.messages.length - 1]!.created_at;
        return (
          <section key={th.key} className="et-thread">
            {multi && (
              <div className="et-thread-head">
                <span className="ico"><MessagesSquare size={14} strokeWidth={1.75} /></span>
                <span className="et-thread-subj" title={th.subject}>{th.subject || t('et.noSubject')}</span>
                <span className="et-thread-n">· {t('et.msgs', { n: th.messages.length })}</span>
                <span className="et-thread-people" title={people.join(', ')}>
                  {people.map((p) => <span key={p} className="et-avatar" style={{ background: `hsl(${hueOf(p)} 52% 44%)` }}>{initialsOf(displayName(p))}</span>)}
                </span>
                <span className="et-thread-last">{ago(lastAt, now)}</span>
              </div>
            )}
            <div className={`et-thread-list${multi ? '' : ' single'}`}>
              {list.map((m) => (
                <MessageCard key={m.id} m={m} meta={meta.get(m.id)} open={openIds.has(m.id)} onToggle={() => toggle(m.id)}
                  showSubject={!multi} highlight={highlightId === m.id} delay={delay++} now={now}
                  onRecategorize={onRecategorize} onAddToFiles={onAddToFiles} onReply={onReply} busy={busyId === m.id}
                  cardRef={(el) => { if (el) cards.current.set(m.id, el); else cards.current.delete(m.id); }} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );

  const toolbar = (
    <div className="et-toolbar">
      {/* Es un <button> SOLO cuando de verdad recoge el panel; si no, un lector de pantalla anunciaría un botón inerte. */}
      {collapsible && !max ? (
        <button type="button" className="et-title clickable" onClick={() => setPanelOpen(!panelOpen)} aria-expanded={panelOpen}>
          <span className="caret"><ChevronDown size={14} strokeWidth={2} /></span>
          <span className="ico"><History size={13} strokeWidth={1.75} /></span>
          <span>{title ?? t('em.history')}</span>
          {messages.length > 0 && <span className="et-count">{messages.length}</span>}
        </button>
      ) : (
        <div className="et-title">
          <span className="ico"><History size={13} strokeWidth={1.75} /></span>
          <span>{title ?? t('em.history')}</span>
          {messages.length > 0 && <span className="et-count">{messages.length}</span>}
        </div>
      )}
      {(panelOpen || max) && messages.length > 0 && (
        <div className="et-tools">
          <button type="button" className="et-tool" onClick={() => setOrder(order === 'newest' ? 'oldest' : 'newest')} title={order === 'newest' ? t('et.oldestFirst') : t('et.newestFirst')}>
            <ArrowDownUp size={13} strokeWidth={2} /> {order === 'newest' ? t('et.newestFirst') : t('et.oldestFirst')}
          </button>
          <button type="button" className="et-tool" onClick={allOpen ? collapseAll : expandAll} title={allOpen ? t('et.collapseAll') : t('et.expandAll')}>
            {allOpen ? <ChevronsDownUp size={13} strokeWidth={2} /> : <ChevronsUpDown size={13} strokeWidth={2} />} {allOpen ? t('et.collapseAll') : t('et.expandAll')}
          </button>
          {variant === 'panel' && (
            <button type="button" className={`et-tool${max ? ' on' : ''}`} onClick={() => setMax(!max)} title={max ? t('et.minimize') : t('et.maximize')}>
              {max ? <Minimize2 size={13} strokeWidth={2} /> : <Maximize2 size={13} strokeWidth={2} />} {max ? t('et.minimize') : t('et.maximize')}
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (max) {
    return createPortal(
      <>
        <div className="et-backdrop" onClick={() => setMax(false)} role="presentation" />
        <section className="et-panel et-max">{toolbar}{body}</section>
      </>,
      document.body,
    );
  }
  return (
    <section className={`et-panel${variant === 'inline' ? ' et-inline' : ''}${collapsible && !panelOpen ? ' et-closed' : ''}`}>
      {toolbar}
      {(!collapsible || panelOpen) && body}
    </section>
  );
}
